import { useEffect, useState } from "react";
import { isSessionValid } from "@/utils/auth";
import { getStoredProductName, persistProductName } from "@/utils/product";

const workspaceLabel = (import.meta.env.VITE_APP_WORKSPACE || "local").toUpperCase();

export default function DebugUserBanner() {
  const [productName, setProductName] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showAllTips, setShowAllTips] = useState(false);
  const [tipsState, setTipsState] = useState("TIPS-M");
  const [resultsState, setResultsState] = useState("RESULTS-FALSE");
  const [userType, setUserType] = useState("UT-FREE");
  const [tdmState, setTdmState] = useState("TDM-2");

  useEffect(() => {
    const updateBanner = () => {
      const authed = isSessionValid();

      if (!authed) {
        setIsVisible(false);
        setProductName("");
        return;
      }

      setIsVisible(true);
      const storedProduct = getStoredProductName();

      if (storedProduct) {
        setProductName(storedProduct);
        return;
      }

      // Fallback: infer product so dev helper bar never shows "Unknown"
      const freeFlag =
        sessionStorage.getItem("lexiFreeUser") || localStorage.getItem("lexiFreeUser");
      const resolvedProduct = freeFlag ? "Lexi Social" : "Lexi Pro";
      setProductName(resolvedProduct);
      persistProductName(resolvedProduct);
    };

    const updateTipsToggle = () => {
      const savedPreference = localStorage.getItem("showAllTips");
      setShowAllTips(savedPreference === "true");
    };

    const handleStorage = (event) => {
      const keysToWatch = [
        "lexiProduct",
        "authToken",
        "sessionData",
        "verificationSid",
        "showAllTips",
      ];

      // If no key provided (clear) or a watched key changes, refresh banner state
      if (!event.key || keysToWatch.includes(event.key)) {
        updateBanner();
        updateTipsToggle();
      }
    };

    const handleProductEvent = (event) => {
      if (event?.detail?.productName) {
        setProductName(event.detail.productName);
        setIsVisible(isSessionValid());
      } else {
        updateBanner();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        updateBanner();
        updateTipsToggle();
      }
    };

    const handleTipsState = (event) => {
      const detail = event?.detail || {};
      if (detail.userType) setUserType(detail.userType);
      if (detail.tdm) setTdmState(detail.tdm);
      if (detail.tipsState) setTipsState(detail.tipsState);
      if (detail.resultsState) setResultsState(detail.resultsState);
    };

    const intervalId = window.setInterval(updateBanner, 2000);

    updateBanner();
    updateTipsToggle();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("lexi:product-updated", handleProductEvent);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("tipsDisplayChanged", updateTipsToggle);
    window.addEventListener("lexi:tips-state", handleTipsState);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("lexi:product-updated", handleProductEvent);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("tipsDisplayChanged", updateTipsToggle);
      window.removeEventListener("lexi:tips-state", handleTipsState);
      window.clearInterval(intervalId);
    };
  }, []);

  if (!isVisible) return null;

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev);
  };

  if(workspaceLabel === "PROD") return null;

  return (
    <div
      className={`fixed z-[1200] pointer-events-none ${
        isCollapsed ? "top-2 right-2" : "top-0 left-0 right-0"
      }`}
    >
      <div className={isCollapsed ? "" : "mx-auto max-w-md px-2"}>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? "Expand dev helper bar" : "Collapse dev helper bar"}
          className={`pointer-events-auto rounded-lg bg-slate-900/95 text-white text-xs shadow-md backdrop-blur border border-white/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-pink/60 ${
            isCollapsed ? "px-2 py-1 opacity-90" : "mt-2 w-full px-3 py-2 opacity-80"
          }`}
        >
          {isCollapsed ? (
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold bg-brand-pink rounded-md px-2 py-0.5">
                {productName}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold bg-brand-pink rounded-md px-2 py-0.5">
                  {productName}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-300">
                  {workspaceLabel}
                </span>
              </div>
              <div className="flex items-center gap-0">
                <span className="ml-2 rounded bg-white/10 px-0 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-200">
                  {userType} · {tdmState} · {tipsState} · {resultsState}
                </span>
              </div>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
